import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  usePlatformSettings,
  useUpdatePlatformSettings,
  type UpdatePlatformSettingsInput,
} from "@/hooks/usePlatformSettings";

export const LegalLinksSection = () => {
  const { data, isLoading } = usePlatformSettings();
  const update = useUpdatePlatformSettings();

  const [form, setForm] = useState({
    termsUrl: "",
    privacyUrl: "",
    aboutUrl: "",
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      termsUrl: data.termsUrl ?? "",
      privacyUrl: data.privacyUrl ?? "",
      aboutUrl: data.aboutUrl ?? "",
    });
  }, [data]);

  const onSave = () => {
    const payload: UpdatePlatformSettingsInput = {
      termsUrl: form.termsUrl.trim() || null,
      privacyUrl: form.privacyUrl.trim() || null,
      aboutUrl: form.aboutUrl.trim() || null,
    };
    update.mutate(payload, {
      onSuccess: () => toast.success("Legal links saved"),
      onError: (e) => toast.error(e.message),
    });
  };

  if (isLoading) return <Skeleton className="h-32 w-full" />;

  return (
    <section className="rounded-card border border-border p-5 bg-card space-y-4">
      <header>
        <h2 className="text-lg font-semibold">Legal & static links</h2>
        <p className="text-sm text-muted-foreground">
          Update these to change the URLs the mobile apps open for Terms,
          Privacy, and About — no app release required.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Terms & Conditions URL">
          <Input
            type="url"
            placeholder="https://…"
            value={form.termsUrl}
            onChange={(e) => setForm({ ...form, termsUrl: e.target.value })}
          />
        </Field>
        <Field label="Privacy Policy URL">
          <Input
            type="url"
            placeholder="https://…"
            value={form.privacyUrl}
            onChange={(e) => setForm({ ...form, privacyUrl: e.target.value })}
          />
        </Field>
        <Field label="About URL">
          <Input
            type="url"
            placeholder="https://…"
            value={form.aboutUrl}
            onChange={(e) => setForm({ ...form, aboutUrl: e.target.value })}
          />
        </Field>
      </div>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={update.isPending}>
          {update.isPending ? "Saving…" : "Save links"}
        </Button>
      </div>
    </section>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    {children}
  </div>
);

export default LegalLinksSection;
